import { useMutation } from "@tanstack/react-query";
import { RoleWatcher, RoleWatchersResponse, WatcherProfile } from "PFApp/activities/types/watchers";
import { getNextPageParam } from "PFCore/helpers/get_next_page_param";
import {
  fetchActivityWatchers,
  QuestionnaireAnswers,
  removeInterest,
  showInterest,
  ShowInterestResponse
} from "PFCore/services/activities";
import { Activity, InfiniteQueryOptions, OrderOptionOrEmpty } from "PFTypes";
import { useMemo } from "react";

import { useInfiniteQuery } from "../use_infinite_query";
import { activityKeys } from "./query_keys";

export const useInfiniteActivityWatchers = (
  activity: Activity,
  perPage: number = 15,
  filters: Record<string, unknown> = {},
  order: OrderOptionOrEmpty = {},
  options: InfiniteQueryOptions<RoleWatchersResponse> = {}
) => {
  const watchersQuery = useInfiniteQuery({
    queryKey: activityKeys.watchers(activity.id, { filters, order, perPage }),
    queryFn: ({ pageParam }) => fetchActivityWatchers(activity.id, pageParam, perPage, filters, order),
    initialPageParam: 1,
    getNextPageParam,
    ...options
  });

  const watchers = useMemo(
    () =>
      (watchersQuery?.data?.pages?.reduce((acc, current) => [...acc, ...current.entries], []) || []).map(
        (watcher: RoleWatcher) => ({
          ...watcher,
          profile: {
            ...watcher.profile,
            ...(watcher.profile as WatcherProfile).additionalCustomFields
          },
          applicableCustomValues: watcher.applicableCustomValues
        })
      ),
    [watchersQuery?.data?.pages]
  );

  return { ...watchersQuery, watchers };
};

interface ShowInterestVariables {
  activityId: number;
  questionnaireAnswers?: QuestionnaireAnswers;
}

interface RemoveInterestVariables {
  activityId: number;
  interestedId: number;
}

export const useShowInterestMutation = () =>
  useMutation<ShowInterestResponse, unknown, ShowInterestVariables>({
    mutationKey: ["show_interest"],
    mutationFn: ({ activityId, questionnaireAnswers }) => showInterest(activityId, questionnaireAnswers),
    retry: 1
  });

export const useRemoveInterestMutation = () =>
  useMutation<unknown, unknown, RemoveInterestVariables>({
    mutationKey: ["remove_interest"],
    mutationFn: ({ activityId, interestedId }) => removeInterest(activityId, interestedId),
    retry: 1
  });
